import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService, User, UserRole, ApiError } from '../services/apiService';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  guestLogin: () => Promise<{ success: boolean; error?: string }>;
  register: (email: string, password: string, name: string) => Promise<{ success: boolean; error?: string }>;
  loginAdmin: (password: string) => boolean;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
  isAdmin: boolean;
  hasRole: (role: UserRole) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Contraseña hardcodeada para el admin (en producción debería estar en variables de entorno)
const ADMIN_PASSWORD = 'admin123';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Verificar si ya está autenticado al cargar la página
    const initializeAuth = async () => {
      try {
        // Si hay un token, verificar si es válido obteniendo el perfil
        if (apiService.isAuthenticated()) {
          const profileResponse = await apiService.getProfile();
          if (profileResponse.success) {
            setIsAuthenticated(true);
            setUser(profileResponse.data);
          } else {
            // Token inválido, limpiar
            apiService.logout();
          }
        }
      } catch (error) {
        // Error al verificar token, limpiar autenticación
        console.error('Error verifying authentication:', error);
        apiService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await apiService.login(email, password);
      
      if (response.success) {
        setIsAuthenticated(true);
        setUser(response.data.user);
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Error al iniciar sesión' };
      }
    } catch (error) {
      let errorMessage = 'Error de conexión. Intenta nuevamente.';
      
      if (error instanceof ApiError) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        if (error.message.includes('fetch')) {
          errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión.';
        } else {
          errorMessage = 'Ocurrió un error inesperado. Intenta nuevamente.';
        }
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const guestLogin = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await apiService.guestLogin();
      
      if (response.success) {
        setIsAuthenticated(true);
        setUser(response.data.user);
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Error al iniciar sesión como invitado' };
      }
    } catch (error) {
      let errorMessage = 'Error de conexión. Intenta nuevamente.';
      
      if (error instanceof ApiError) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        if (error.message.includes('fetch')) {
          errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión.';
        } else {
          errorMessage = 'Ocurrió un error inesperado. Intenta nuevamente.';
        }
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, name: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await apiService.register(email, password, name);
      
      if (response.success) {
        setIsAuthenticated(true);
        setUser(response.data.user);
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Error al registrar usuario' };
      }
    } catch (error) {
      let errorMessage = 'Error de conexión. Intenta nuevamente.';
      
      if (error instanceof ApiError) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        if (error.message.includes('fetch')) {
          errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión.';
        } else {
          errorMessage = 'Ocurrió un error inesperado. Intenta nuevamente.';
        }
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const loginAdmin = (password: string): boolean => {
    // Verificar credenciales de admin
    if (password === ADMIN_PASSWORD) {
      const adminUser: User = {
        id: 'admin',
        name: 'Administrador',
        email: 'admin@zona-virtual.com',
        role: 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      setIsAuthenticated(true);
      setUser(adminUser);
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setError(null);
    apiService.logout();
  };

  // Funciones de verificación de roles
  const isAdmin = user?.role === 'admin';
  const hasRole = (role: UserRole): boolean => user?.role === role;

  const value = {
    isAuthenticated,
    user,
    login,
    guestLogin,
    register,
    loginAdmin,
    logout,
    isLoading,
    error,
    isAdmin,
    hasRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 